export const DEFAULT_CONVERSION_CURRENCY = "USD";

export function cleanConversionText(value) {
  return String(value || "").trim();
}

export function cleanConversionCurrency(currency) {
  const currencyCode = cleanConversionText(currency).toUpperCase();
  return /^[A-Z]{3}$/.test(currencyCode)
    ? currencyCode
    : DEFAULT_CONVERSION_CURRENCY;
}

export function cleanConversionIdentifier(value) {
  return cleanConversionText(value)
    .toLowerCase()
    .replace(/[^a-z0-9_:-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function isLocalConversionHostname(hostname) {
  const host = cleanConversionText(hostname).toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]" ||
    host.endsWith(".local")
  );
}

export function centsToDollars(cents) {
  const numericCents = Number(cents);
  if (!Number.isFinite(numericCents)) return 0;
  return Math.max(0, numericCents / 100);
}

export function normalizeDollars(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, numericValue);
}

export function resolveConversionValue({ value = null, valueCents = null } = {}) {
  return valueCents !== null && valueCents !== undefined
    ? centsToDollars(valueCents)
    : normalizeDollars(value);
}

export function normalizeConversionEmail(email) {
  const normalizedEmail = cleanConversionText(email).toLowerCase();
  const [localPart, domain] = normalizedEmail.split("@");
  if (!localPart || !domain) return normalizedEmail;

  if (!["gmail.com", "googlemail.com"].includes(domain)) {
    return normalizedEmail;
  }

  return `${localPart.replace(/\+.*/, "").replace(/\./g, "")}@${domain}`;
}

export function normalizeConversionPhone(
  phone,
  { defaultCountryCode = "1" } = {},
) {
  const rawPhone = cleanConversionText(phone);
  if (!rawPhone) return "";

  const digits = rawPhone.replace(/\D+/g, "");
  if (!digits) return "";

  const countryCode = String(defaultCountryCode || "").replace(/\D+/g, "");
  const e164Digits =
    rawPhone.startsWith("+") || digits.length > 10
      ? digits
      : `${countryCode}${digits}`;

  return /^\d{11,15}$/.test(e164Digits) ? `+${e164Digits}` : "";
}

export function normalizeConversionCountryCode(country, fallback = "US") {
  const cleanCountry = cleanConversionText(country).toUpperCase();
  if (/^[A-Z]{2}$/.test(cleanCountry)) return cleanCountry;
  if (
    ["USA", "UNITED STATES", "UNITED STATES OF AMERICA"].includes(cleanCountry)
  ) {
    return "US";
  }
  if (cleanCountry) return "";
  return cleanConversionText(fallback).toUpperCase() || "";
}

export function omitEmptyConversionFields(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (typeof value === "object" && !Array.isArray(value)) {
      if (Object.keys(value).length === 0) return;
      out[key] = value;
      return;
    }
    out[key] = value;
  });
  return out;
}

export function buildConversionEventId({
  scope,
  eventName,
  transactionId = null,
  subjectId = null,
  value = null,
  valueCents = null,
  method = null,
} = {}) {
  const cleanScope = cleanConversionIdentifier(scope) || "conversion";
  const cleanEventName = cleanConversionIdentifier(eventName) || "event";
  const cleanTransactionId = cleanConversionText(transactionId);
  if (cleanTransactionId) {
    return `${cleanScope}_${cleanEventName}_${cleanTransactionId}`;
  }

  const subjectKey = cleanConversionIdentifier(subjectId) || "anonymous";
  const amountKey = Math.round(
    resolveConversionValue({ value, valueCents }) * 100,
  );
  const methodKey = cleanConversionIdentifier(method);

  return `${cleanScope}_${cleanEventName}_${subjectKey}_${amountKey}${
    methodKey ? `_${methodKey}` : ""
  }`;
}
